/* eslint-disable @typescript-eslint/no-explicit-any */
import { appLocales, Locales } from "@/lib/i18n";
import { __ } from "@/lib/i18n/functions";
import { segments } from "@/locales/segment";
import { CategorieDocument } from "@/prismicio-types";
import { PrismicRichText } from "@prismicio/react";
import Image from "next/image";
import Link from "next/link";

interface CategoriesListingProps {
  categoryData: {
    title: string;
    longDescription: any;
  };
  category: any;
  defaultCategory?: any;
  categoryTree?: CategorieDocument[];
  categoryHandle?: string;
  currentLocale: Locales;
}

const CategoriesListing = ({
  categoryData,
  category,
  categoryHandle,
  currentLocale,
}: CategoriesListingProps) => {
  const homeHref = currentLocale === appLocales.defaultLocale ? "/" : `/${currentLocale}/`;

  return (
    <div className="container flex w-full flex-row gap-5 px-5 py-10">
      <div className="w-full">
        <h1 className="h2 mb-3">{categoryData.title}</h1>

        <PrismicRichText field={categoryData.longDescription} />
        {category?.children?.length > 0 ? (
          <>
            <span className="mt-3 block text-sm text-[#9C9C9C]">
              {category?.children?.length} {__("Categories", currentLocale)}
            </span>

            <ul className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {category?.children
                ?.sort((a: CategorieDocument, b: CategorieDocument) => {
                  if (currentLocale !== appLocales.defaultLocale) {
                    const displayA = a?.data?.display;
                    const displayB = b?.data?.display;

                    // If both are undefined, maintain original order
                    if (displayA == null && displayB == null) {
                      return 0;
                    }

                    // If only a is undefined, put it after b
                    if (displayA == null) {
                      return 1;
                    }

                    // If only b is undefined, put it after a
                    if (displayB == null) {
                      return -1;
                    }

                    // Both have values, sort in ascending order
                    if (displayA < displayB) {
                      return -1;
                    }
                    if (displayA > displayB) {
                      return 1;
                    }
                    return 0;
                  }

                  return 0;
                })
                ?.map((child: any) => {
                  return (
                    <li key={child.id} className="group relative flex flex-col-reverse gap-[0.625rem]">
                      <h3 className="font-geist text-center text-lg uppercase">{child.data.title}</h3>
                      <figure className="aspect-[25/16] w-full overflow-hidden rounded-md bg-[#F9F7F5]">
                        {child?.data?.featuredImage?.url && (
                          <Image
                            src={child.data.featuredImage.url}
                            alt={child.data.featuredImage.alt || child.data.title}
                            width={child.data.featuredImage.dimensions.width}
                            height={child.data.featuredImage.dimensions.height}
                            className="h-full w-full bg-white object-contain transition group-hover:scale-105"
                          />
                        )}
                      </figure>
                      <Link
                        href={`${homeHref}${segments[currentLocale || "fr"].categories}/${categoryHandle}/${child.uid}`}
                        className="absolute inset-0 z-10"
                      />
                    </li>
                  );
                })}
            </ul>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default CategoriesListing;
